import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';

const EVENTS_COLLECTION = 'events';

/**
 * Convert Firestore timestamp to JavaScript Date
 */
const timestampToDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  return timestamp;
};

/**
 * Convert JavaScript Date to Firestore Timestamp
 */
const dateToTimestamp = (date) => {
  if (!date) return null;
  if (date instanceof Date) {
    return Timestamp.fromDate(date);
  }
  return date;
};

/**
 * Add a new event
 */
export const addEvent = async (eventData) => {
  try {
    const data = {
      ...eventData,
      eventDate: dateToTimestamp(eventData.eventDate),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    const docRef = await addDoc(collection(db, EVENTS_COLLECTION), data);
    return { id: docRef.id, ...data };
  } catch (error) {
    console.error('Error adding event:', error);
    throw error;
  }
};

/**
 * Update an existing event
 */
export const updateEvent = async (eventId, eventData) => {
  try {
    const data = {
      ...eventData,
      eventDate: dateToTimestamp(eventData.eventDate),
      updatedAt: Timestamp.now()
    };
    await updateDoc(doc(db, EVENTS_COLLECTION, eventId), data);
    return { id: eventId, ...data };
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

/**
 * Delete an event
 */
export const deleteEvent = async (eventId) => {
  try {
    await deleteDoc(doc(db, EVENTS_COLLECTION, eventId));
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

/**
 * Get all events for a user
 */
export const getEvents = async (userId, filters = {}) => {
  try {
    // Always filter by userId first, then order by date
    let q = query(
      collection(db, EVENTS_COLLECTION),
      where('userId', '==', userId),
      orderBy('eventDate', 'desc')
    );

    const querySnapshot = await getDocs(q);
    let events = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      eventDate: timestampToDate(doc.data().eventDate),
      createdAt: timestampToDate(doc.data().createdAt),
      updatedAt: timestampToDate(doc.data().updatedAt)
    }));

    // Apply client-side filters
    if (filters.category) {
      events = events.filter(event => event.category === filters.category);
    }

    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      events = events.filter(event =>
        event.title?.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower) ||
        event.location?.toLowerCase().includes(searchLower) ||
        event.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (filters.startDate) {
      events = events.filter(event => event.eventDate >= filters.startDate);
    }

    if (filters.endDate) {
      events = events.filter(event => event.eventDate <= filters.endDate);
    }

    if (filters.tags && filters.tags.length > 0) {
      const filterTagsLower = filters.tags.map(t => t.toLowerCase());
      events = events.filter(event =>
        event.tags && event.tags.some(tag => filterTagsLower.includes(tag.toLowerCase()))
      );
    }

    return events;
  } catch (error) {
    console.error('Error getting events:', error);
    throw error;
  }
};

/**
 * Export events to CSV format
 */
export const exportEventsToCSV = (events) => {
  const headers = ['Date', 'Title', 'Category', 'Location', 'Description', 'Tags'];
  const rows = events.map(event => [
    event.eventDate ? new Date(event.eventDate).toLocaleDateString() : '',
    event.title || '',
    event.category || '',
    event.location || '',
    event.description || '',
    event.tags ? event.tags.join('; ') : ''
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Export events to JSON format
 */
export const exportEventsToJSON = (events) => {
  return JSON.stringify(events, null, 2);
};

